import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { useCategories, useMasterData } from '../masterdata/queries';
import { flattenCategories } from '../tickets/presentation';
import { useCreateTemplate, useDeleteTemplate, useTemplates, useUpdateTemplate } from './queries';
import { Select } from '../../components/Select';

/**
 * Verwaltung der Ticketvorlagen -- F1-37, K-19.
 *
 * Die Maske bietet genau die Felder an, die der Server zulaesst. Ein
 * Freitextfeld fuer beliebiges JSON waere kuerzer zu bauen und in der
 * Anwendung unbrauchbar: Wer eine Vorlage anlegt, kennt Feldnamen und
 * Kennungen nicht auswendig.
 *
 * **Der Zustand fehlt mit Absicht.** Ein neues Ticket startet immer im
 * Vorgabezustand des Mandanten -- eine Vorlage, die ein Ticket bereits
 * geschlossen anlegt, waere ein Vorgang ohne Vorgeschichte.
 */
export function TemplatesPage() {
  const { t } = useTranslation();
  const templates = useTemplates();
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const remove = useDeleteTemplate();

  const queues = useMasterData('queue');
  const types = useMasterData('type');
  const priorities = useMasterData('priority');
  const categories = useCategories();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaults, setDefaults] = useState<Record<string, string>>({
    title: '',
    description: '',
    queue_id: '',
    type_id: '',
    priority_id: '',
    category_id: '',
  });

  const setDefault = (field: string, value: string) => {
    setDefaults((current) => ({ ...current, [field]: value }));
  };

  return (
    <>
      <PageHeader title={t('templates.title')} subtitle={t('templates.subtitle')} />

      <section className="card">
        <h2>{t('templates.new')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="template-name">
            {t('masterdata.name')}
          </label>
          <input
            id="template-name"
            className="field__control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="template-description">
            {t('templates.description')}
          </label>
          <input
            id="template-description"
            className="field__control"
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </div>

        <h3>{t('templates.defaults')}</h3>

        <div className="field">
          <label className="field__label" htmlFor="template-title">
            {t('tickets.subject')}
          </label>
          <input
            id="template-title"
            className="field__control"
            value={defaults.title}
            onChange={(event) => {
              setDefault('title', event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="template-body">
            {t('tickets.description')}
          </label>
          <textarea
            id="template-body"
            className="field__control field__control--area"
            rows={5}
            value={defaults.description}
            onChange={(event) => {
              setDefault('description', event.target.value);
            }}
          />
        </div>

        <TemplateSelect
          id="template-queue"
          label={t('tickets.queue')}
          value={defaults.queue_id ?? ''}
          empty={t('templates.keepDefault')}
          options={(queues.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }))}
          onChange={(value) => {
            setDefault('queue_id', value);
          }}
        />

        <TemplateSelect
          id="template-type"
          label={t('tickets.type')}
          value={defaults.type_id ?? ''}
          empty={t('templates.keepDefault')}
          options={(types.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }))}
          onChange={(value) => {
            setDefault('type_id', value);
          }}
        />

        <TemplateSelect
          id="template-priority"
          label={t('tickets.priority')}
          value={defaults.priority_id ?? ''}
          empty={t('templates.keepDefault')}
          options={(priorities.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }))}
          onChange={(value) => {
            setDefault('priority_id', value);
          }}
        />

        <TemplateSelect
          id="template-category"
          label={t('tickets.category')}
          value={defaults.category_id ?? ''}
          empty={t('templates.keepDefault')}
          options={flattenCategories(categories.data ?? [])}
          onChange={(value) => {
            setDefault('category_id', value);
          }}
        />

        {create.isError ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={!name.trim()}
          busy={create.isPending}
          onClick={() => {
            // Leere Felder werden nicht mitgeschickt: Eine Vorlage, die ein
            // Feld auf den Leerwert setzt, unterschiede sich sonst nicht von
            // einer, die es offen laesst -- und ueberschriebe die Vorgabe des
            // Mandanten mit nichts.
            const filled = Object.fromEntries(
              Object.entries(defaults).filter(([, value]) => value !== ''),
            );
            create.mutate(
              { name: name.trim(), description, defaults: filled, is_active: true, sort_order: 0 },
              {
                onSuccess: () => {
                  setName('');
                  setDescription('');
                  setDefaults({
                    title: '',
                    description: '',
                    queue_id: '',
                    type_id: '',
                    priority_id: '',
                    category_id: '',
                  });
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>

      <section className="card card--flush">
        {templates.isPending ? <SkeletonTable columns={4} /> : null}
        {templates.isError ? <ErrorNotice error={templates.error} /> : null}

        {templates.data?.length === 0 ? (
          <EmptyState
            icon="templates"
            title={t('templates.empty')}
            hint={t('templates.emptyHint')}
          />
        ) : null}

        {templates.data && templates.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('masterdata.name')}</th>
                  <th scope="col">{t('templates.description')}</th>
                  <th scope="col">{t('masterdata.isActive')}</th>
                  <th scope="col">{t('tickets.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {templates.data.map((template) => (
                  <tr key={template.id}>
                    <td>{template.name}</td>
                    <td className="table__muted">{template.description}</td>
                    <td>
                      <Badge tone={template.is_active ? 'success' : 'neutral'}>
                        {template.is_active ? t('masterdata.yes') : t('masterdata.no')}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          update.mutate({
                            templateId: template.id,
                            payload: { is_active: !template.is_active },
                          });
                        }}
                      >
                        {template.is_active ? t('snippets.deactivate') : t('snippets.activate')}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          remove.mutate(template.id);
                        }}
                      >
                        {t('masterdata.delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  );
}

interface TemplateSelectProps {
  id: string;
  label: string;
  value: string;
  empty: string;
  options: { id: string; label: string }[];
  onChange: (value: string) => void;
}

function TemplateSelect({ id, label, value, empty, options, onChange }: TemplateSelectProps) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <Select
        id={id}
        className="field__control"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        <option value="">{empty}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}
